import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import {
  Alert,
  AppBar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Paper,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest } from "../utils/apiRequest";
import { showToast } from "../utils/toastOptions";

const SlideUp = forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const STATUS_STYLES = {
  new: { border: "#2e7d32", bg: "rgba(46,125,50,0.06)", label: "New" },
  existing: { border: "#1976d2", bg: "rgba(25,118,210,0.06)", label: "Existing" },
  invalid: { border: "#d32f2f", bg: "rgba(211,47,47,0.06)", label: "Invalid" },
};

export function PreviewSummary({ summary, statusStyles = STATUS_STYLES }) {
  const stats = [
    { key: "new", count: summary.new || 0, color: statusStyles.new.border, label: statusStyles.new.label },
    { key: "existing", count: summary.existing || 0, color: statusStyles.existing.border, label: statusStyles.existing.label },
    { key: "invalid", count: summary.invalid || 0, color: statusStyles.invalid.border, label: statusStyles.invalid.label },
  ];
  return (
    <Stack direction="row" spacing={3} alignItems="center">
      {stats.map((s) => (
        <Stack key={s.key} direction="row" spacing={0.75} alignItems="baseline">
          <Typography variant="h5" sx={{ fontWeight: 700, color: s.color, lineHeight: 1 }}>
            {s.count}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {s.label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function FileErrors({ errors }) {
  if (!errors?.length) return null;
  return (
    <Alert severity="error">
      <Typography fontWeight={600}>CSV could not be processed:</Typography>
      <ul style={{ margin: 0 }}>
        {errors.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </Alert>
  );
}

export function UploadDropzone({
  file,
  onFileChange,
  helperText,
  inputId,
  onDownloadTemplate,
  dense = false,
  title,
}) {
  return (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 1,
        px: dense ? 2 : 3,
        py: dense ? 1.5 : 3,
        textAlign: "center",
        height: dense ? "100%" : undefined,
        display: dense ? "flex" : "block",
        flexDirection: "column",
        justifyContent: "center",
        boxSizing: "border-box",
      }}
    >
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: dense ? 1 : 1.5 }}>
          {title}
        </Typography>
      )}
      <input
        type="file"
        accept=".csv,text/csv"
        id={inputId}
        style={{ display: "none" }}
        onChange={(e) => onFileChange(e.target.files?.[0] || null)}
      />
      <Stack direction="row" spacing={1.5} justifyContent="center">
        <label htmlFor={inputId}>
          <Button
            component="span"
            variant="contained"
            color="success"
            size={dense ? "small" : "medium"}
            startIcon={<CloudUploadIcon />}
          >
            Choose CSV file
          </Button>
        </label>
        {onDownloadTemplate && (
          <Button
            variant="outlined"
            color="success"
            size={dense ? "small" : "medium"}
            startIcon={<DownloadIcon />}
            onClick={onDownloadTemplate}
          >
            Download Template
          </Button>
        )}
      </Stack>
      <Typography sx={{ mt: dense ? 1 : 2 }} variant="body2" color="text.secondary">
        {file ? file.name : "No file selected"}
      </Typography>
      {helperText && (
        <Typography sx={{ mt: dense ? 0.5 : 1 }} variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function stripBlankIds(row) {
  const out = { ...row };
  if (out.company_id === "" || out.company_id == null) {
    delete out.company_id;
  }
  return out;
}

const HIDDEN_RESULT_KEYS = new Set(["change_log_id"]);

export function CommitResult({ result, onReset, onViewChangeLog }) {
  const failed = result?.failed || [];
  const changeLogId = result?.change_log_id;
  return (
    <Stack spacing={2}>
      <Alert severity={failed.length ? "warning" : "success"}>
        {failed.length
          ? `${failed.length} row${failed.length === 1 ? "" : "s"} failed; see details below.`
          : "All rows processed successfully."}
      </Alert>
      <Box>
        {Object.entries(result).map(([k, v]) => {
          if (typeof v !== "number" || HIDDEN_RESULT_KEYS.has(k)) return null;
          return (
            <Chip
              key={k}
              size="small"
              sx={{ mr: 1, mb: 1 }}
              label={`${k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: ${v}`}
            />
          );
        })}
        {Array.isArray(result.created) && (
          <Chip
            size="small"
            color="success"
            sx={{ mr: 1, mb: 1 }}
            label={`Created ${result.created.length}`}
          />
        )}
        {Array.isArray(result.existing) && (
          <Chip
            size="small"
            color="info"
            sx={{ mr: 1, mb: 1 }}
            label={`Existing ${result.existing.length}`}
          />
        )}
      </Box>
      {failed.length > 0 && (
        <Alert severity="warning">
          <ul style={{ margin: 0 }}>
            {failed.map((f) => (
              <li key={f.row_number}>
                Row {f.row_number} ({f.email_address || f.email}): {f.message}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {changeLogId != null && onViewChangeLog && (
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => onViewChangeLog(changeLogId)}
          >
            View Change Log
          </Button>
        )}
        <Button variant="contained" onClick={onReset}>
          Start another import
        </Button>
      </Box>
    </Stack>
  );
}

/**
 * Generic editable-preview workflow for CSV import / bulk operations.
 *
 * Modes:
 * - File upload: user picks a CSV -> POST multipart to previewUrl
 * - Programmatic rows: caller passes initialRows -> POST JSON to previewUrl
 * - Inline edit -> POST JSON {rows} to previewUrl to re-validate
 * - Commit -> POST JSON {rows} to commitUrl
 */
export default function CsvWorkflow({
  helperText,
  previewUrl,
  commitUrl,
  editableColumns,
  buildCommitMessage,
  compact = false,
  onCommitSuccess,
  onDownloadTemplate,
  statusStyles = STATUS_STYLES,
  initialRows = null,
  hideUpload = false,
  dense = false,
  title,
  reviewTitle,
  renderLayout,
  onViewChangeLog,
}) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editedRows, setEditedRows] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (preview?.rows) {
      setEditedRows(
        preview.rows.map((r) => ({
          ...r,
          company_id: r.company_id == null ? "" : String(r.company_id),
        }))
      );
    } else {
      setEditedRows([]);
    }
  }, [preview]);

  const removeRow = useCallback((rowIdx) => {
    setEditedRows((prev) => prev.filter((_, i) => i !== rowIdx));
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setEditedRows([]);
    setResult(null);
    uploadedFileRef.current = null;
  }, []);

  const uploadedFileRef = useRef(null);

  // File upload mode
  useEffect(() => {
    if (!file || file === uploadedFileRef.current) return;
    uploadedFileRef.current = file;
    let cancelled = false;
    setPreviewing(true);
    setResult(null);
    const form = new FormData();
    form.append("file", file);
    apiRequest(previewUrl, { method: "POST", body: form })
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((e) => {
        if (!cancelled) showToast("error", e.message || "Failed to preview CSV");
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [file, previewUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Programmatic rows mode (from table selections)
  const initialRowsRef = useRef(null);
  useEffect(() => {
    if (!initialRows || initialRows === initialRowsRef.current) return;
    initialRowsRef.current = initialRows;
    let cancelled = false;
    setPreviewing(true);
    setResult(null);
    apiRequest(previewUrl, {
      method: "POST",
      body: { rows: initialRows },
    })
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((e) => {
        if (!cancelled) showToast("error", e.message || "Failed to preview");
      })
      .finally(() => {
        if (!cancelled) setPreviewing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialRows, previewUrl]);

  const handleRevalidate = useCallback(async () => {
    if (!editedRows.length || previewing) return;
    setPreviewing(true);
    try {
      const data = await apiRequest(previewUrl, {
        method: "POST",
        body: { rows: editedRows.map(stripBlankIds) },
      });
      setPreview(data);
    } catch (e) {
      showToast("error", e.message || "Re-validation failed");
    } finally {
      setPreviewing(false);
    }
  }, [editedRows, previewing, previewUrl]);

  const handleCommit = useCallback(async () => {
    if (committing || !editedRows.length) return;
    setCommitting(true);
    try {
      const data = await apiRequest(commitUrl, {
        method: "POST",
        body: { rows: editedRows.map(stripBlankIds) },
      });
      setResult(data);
      showToast("success", buildCommitMessage(data));
      if (onCommitSuccess) onCommitSuccess();
    } catch (e) {
      showToast("error", e.message || "Submit failed");
    } finally {
      setCommitting(false);
    }
  }, [editedRows, committing, commitUrl, buildCommitMessage, onCommitSuccess]);

  const summary = useMemo(() => {
    const s = { new: 0, existing: 0, invalid: 0 };
    for (const r of editedRows) {
      const cls = r.classification || "invalid";
      if (cls in s) s[cls]++;
    }
    return s;
  }, [editedRows]);
  const fileErrors = preview?.file_errors || [];
  const subscriptionWarnings = preview?.subscription_warnings || [];
  const hasInvalid = summary.invalid > 0;
  const hasFileErrors = fileErrors.length > 0;
  const commitDisabled =
    committing ||
    hasInvalid ||
    hasFileErrors ||
    subscriptionWarnings.length > 0 ||
    summary.new + summary.existing === 0;

  const setCell = (rowIdx, key, value) => {
    setEditedRows((prev) => {
      const next = prev.slice();
      next[rowIdx] = { ...next[rowIdx], [key]: value };
      return next;
    });
  };

  if (result && !renderLayout) {
    return <CommitResult result={result} onReset={reset} onViewChangeLog={onViewChangeLog} />;
  }

  const renderDropzone = () => {
    if (hideUpload) {
      if (previewing) {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        );
      }
      return null;
    }
    if (compact) {
      return (
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            px: 3,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            boxSizing: "border-box",
            textAlign: "center" 
          }}
        >
          {title && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "center" }}>
              {title}
            </Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
            <input
              type="file"
              accept=".csv,text/csv"
              id={`upload-${previewUrl}`}
              style={{ display: "none" }}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <label htmlFor={`upload-${previewUrl}`}>
              <Button
                component="span"
                variant="contained"
                color="success"
                size="small"
                startIcon={<CloudUploadIcon />}
              >
                Import CSV
              </Button>
            </label>
            {onDownloadTemplate && (
              <Button
                variant="outlined"
                color="success"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={onDownloadTemplate}
              >
                Download Template
              </Button>
            )}
            {previewing && <CircularProgress size={20} />}
          </Box>
          <Typography sx={{ pt: 1 }} variant="body2" color="text.secondary">
            {file ? file.name : helperText}
          </Typography>
        </Box>
      );
    }
    return (
      <Stack spacing={dense ? 1 : 2} sx={dense ? { height: "100%" } : undefined}>
        <UploadDropzone
          file={file}
          onFileChange={setFile}
          inputId={`upload-${previewUrl}`}
          helperText={helperText}
          onDownloadTemplate={onDownloadTemplate}
          dense={dense}
          title={title}
        />
        {previewing && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
      </Stack>
    );
  };

  if (!preview && !result) {
    if (renderLayout) return renderLayout({ dropzone: renderDropzone(), review: null });
    return renderDropzone();
  }

  const styles = statusStyles;

  const tableContent = (maxHeight) => (
    <TableContainer
      component={fullscreen ? undefined : Paper}
      variant={fullscreen ? undefined : "outlined"}
      sx={{ maxHeight, flex: 1, overflow: "auto", "& .MuiTableCell-root": { py: 0.5, px: 1.5 } }}
    >
      <Table size="small" stickyHeader sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            {editableColumns.map((c) => (
              <TableCell
                key={c.key}
                sx={{
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  overflow: "hidden",
                  width: c.width,
                  ...(c.type === "checkbox" ? { textAlign: "center" } : {}),
                }}
              >
                {c.label}
              </TableCell>
            ))}
            <TableCell sx={{ width: 40, p: 0 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {editedRows.map((r, idx) => {
            const style = styles[r.classification] || styles.invalid;
            const errorList = Array.isArray(r.errors) ? r.errors : [];
            const hasErrors = errorList.length > 0;
            const cellText = (value) =>
              value == null || typeof value === "object" ? "" : value;
            const row = (
              <TableRow
                key={r.row_number}
                sx={{
                  bgcolor: style.bg,
                  "& td:first-of-type": { borderLeft: `4px solid ${style.border}` },
                }}
              >
                {editableColumns.map((c) => (
                  <TableCell
                    key={c.key}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      ...(c.type === "checkbox" ? { textAlign: "center" } : {}),
                    }}
                  >
                    {c.type === "checkbox" ? (
                      <Checkbox
                        size="small"
                        checked={String(r[c.key] ?? "").toLowerCase() === "y" || r[c.key] === true}
                        onChange={(e) => setCell(idx, c.key, e.target.checked ? "Y" : "N")}
                      />
                    ) : (
                      <TextField
                        variant="standard"
                        fullWidth
                        size="small"
                        value={cellText(r[c.key])}
                        onChange={(e) => setCell(idx, c.key, e.target.value)}
                        InputProps={{ disableUnderline: !hasErrors, sx: { fontSize: "0.8125rem" } }}
                      />
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ px: 0.5 }}>
                  <Tooltip title="Remove row">
                    <IconButton
                      size="small"
                      onClick={() => removeRow(idx)}
                      sx={{ opacity: 0.5, "&:hover": { opacity: 1, color: "error.main" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
            if (!hasErrors) return row;
            return (
              <Tooltip
                key={r.row_number}
                title={errorList.join("; ")}
                arrow
                placement="top"
                followCursor
                slotProps={{ tooltip: { sx: { maxWidth: 400, fontSize: "0.8125rem" } } }}
              >
                {row}
              </Tooltip>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const actionButtons = (
    <>
      <Button
        variant="outlined"
        onClick={handleRevalidate}
        disabled={previewing || committing || !editedRows.length}
      >
        {previewing ? <CircularProgress size={18} /> : "Re-validate"}
      </Button>
      <Button variant="contained" disabled={commitDisabled} onClick={handleCommit}>
        {committing ? <CircularProgress size={20} /> : "Submit"}
      </Button>
    </>
  );

  const review = (
    <>
      <Stack spacing={2} sx={compact || renderLayout ? undefined : { flex: 1, minHeight: 0, overflow: "hidden" }}>
        <FileErrors errors={fileErrors} />
        {hasFileErrors && !hideUpload && (
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={reset}>
              Choose another file
            </Button>
          </Box>
        )}
        {subscriptionWarnings.length > 0 && (
          <Alert severity="warning">
            <Typography fontWeight={600} sx={{ mb: 0.5 }}>
              Subscription Required
            </Typography>
            <ul style={{ margin: 0 }}>
              {subscriptionWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </Alert>
        )}
        {!hasFileErrors && (
          <>
            {reviewTitle && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {reviewTitle}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <PreviewSummary summary={summary} statusStyles={styles} />
                {hasInvalid && (
                  <Typography variant="body2" color="error">
                    Fix the invalid rows below, then click Re-validate.
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Fullscreen">
                  <IconButton onClick={() => setFullscreen(true)} size="small">
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
                <Button onClick={reset} disabled={previewing || committing}>
                  Start over
                </Button>
              </Stack>
            </Stack>
            {compact && summary.existing > 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                {summary.existing} existing user{summary.existing !== 1 ? "s" : ""} found —
                submitting will also update their name and subscriptions. Remove those rows if you
                don&apos;t want to update them.
              </Alert>
            )}
            <Box sx={compact || renderLayout ? undefined : { flex: 1, minHeight: 0, overflow: "hidden" }}>
              {tableContent(renderLayout ? 360 : compact ? undefined : "100%")}
            </Box>
          </>
        )}
        <Box
          sx={{
            display: hasFileErrors ? "none" : "flex",
            gap: 1,
            justifyContent: "flex-end",
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
            py: 1.5,
            px: 2,
            mx: -2,
            borderTop: 1,
            borderColor: "divider",
            zIndex: 1,
          }}
        >
          {actionButtons}
        </Box>
      </Stack>

      <Dialog
        fullScreen
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        TransitionComponent={SlideUp}
      >
        <AppBar sx={{ position: "relative" }} color="default" elevation={1}>
          <Toolbar sx={{ gap: 2 }}>
            <IconButton edge="start" onClick={() => setFullscreen(false)}>
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              CSV Preview
            </Typography>
            <PreviewSummary summary={summary} statusStyles={styles} />
            {hasInvalid && (
              <Typography variant="body2" color="error" sx={{ ml: 1 }}>
                Fix invalid rows, then re-validate.
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {actionButtons}
          </Toolbar>
        </AppBar>
        <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
          <FileErrors errors={fileErrors} />
          {!hasFileErrors && tableContent(undefined)}
        </Box>
      </Dialog>
    </>
  );

  if (renderLayout) {
    return renderLayout({
      dropzone: renderDropzone(),
      review: (
        <Box sx={{ mb: 1 }}>
          {result ? (
            <CommitResult result={result} onReset={reset} onViewChangeLog={onViewChangeLog} />
          ) : (
            review
          )}
        </Box>
      ),
    });
  }

  return review;
}
